import { useEffect, useState } from 'react';
import { ShieldCheck, Loader2, ScrollText, MapPin, FileText, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { analytics } from '../lib/api';

/** Admin Compliance Heatmap — one-glance legal snapshot: ToS signed, Vault
 *  on file, DL expiring, and monthly Evidence-PDF coverage per store. */
export default function AdminComplianceHeatmap() {
  const [data, setData] = useState(null);
  useEffect(() => { analytics.heatmap().then(setData); }, []);

  if (!data) return <div className="inline-flex items-center gap-2 text-ink/50"><Loader2 className="w-4 h-4 animate-spin"/>Loading heatmap…</div>;
  const s = data.summary;

  const StatCard = ({ label, value, sub, tone = 'ink' }) => {
    const cls = {
      good: 'bg-[#E8F5E9] text-[#1B5E20] border-[#A5D6A7]',
      warn: 'bg-[#FFF3E0] text-[#8D3A0A] border-[#FFCC80]',
      bad: 'bg-[#FFEBEE] text-[#B71C1C] border-[#EF9A9A]',
      ink: 'bg-white text-ink border-ink/15',
    }[tone];
    return (
      <div className={`rounded-lg border-2 p-3 ${cls}`}>
        <div className="text-[10px] uppercase tracking-[0.2em] font-black opacity-80">{label}</div>
        <div className="font-heading font-black text-2xl mt-0.5">{value}</div>
        {sub && <div className="text-[11px] opacity-70">{sub}</div>}
      </div>
    );
  };

  return (
    <div data-testid="admin-compliance-heatmap">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Total stores" value={s.total_stores}/>
        <StatCard label="Medical stores" value={s.medical_stores} tone="ink"/>
        <StatCard label="ToS signed" value={`${s.tos_signed}/${s.total_stores}`} tone={s.tos_signed === s.total_stores ? 'good' : 'warn'}/>
        <StatCard label="Vault on file" value={`${s.vault_on_file}/${s.total_stores}`} tone={s.vault_on_file === s.total_stores ? 'good' : 'warn'}/>
        <StatCard label="DL expiring <30d" value={s.dl_expiring_30d} tone={s.dl_expiring_30d ? 'warn' : 'good'}/>
        <StatCard label="DL expired" value={s.dl_expired} tone={s.dl_expired ? 'bad' : 'good'}/>
        <StatCard label="Full Evidence-PDF (30d)" value={s.evidence_pdf_ready} sub={`of ${s.medical_stores} medical`} tone={s.evidence_pdf_ready === s.medical_stores ? 'good' : 'warn'}/>
      </div>

      <div className="rounded-lg border border-ink/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[980px]">
          <thead className="bg-muted text-[10px] uppercase tracking-wider font-black text-ink/60">
            <tr>
              <th className="text-left px-3 py-2">Store</th>
              <th className="text-left px-3 py-2">Owner</th>
              <th className="text-left px-3 py-2">Category</th>
              <th className="text-center px-3 py-2">ToS</th>
              <th className="text-center px-3 py-2">Vault</th>
              <th className="text-left px-3 py-2">DL#</th>
              <th className="text-left px-3 py-2">DL expiry</th>
              <th className="text-right px-3 py-2">PDFs 30d</th>
              <th className="text-center px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map(r => (
              <tr key={r.store_id} data-testid={`heatmap-row-${r.store_id}`} className="border-t border-ink/5 hover:bg-muted/50">
                <td className="px-3 py-2 font-bold text-ink truncate max-w-[220px]">
                  {r.store_name}
                  <div className="text-[10px] text-ink/50 font-mono">{r.village || '—'} · {r.pincode || '—'}</div>
                </td>
                <td className="px-3 py-2 text-[11px] text-ink/70 truncate max-w-[140px]">
                  {r.owner_name || '—'}
                  <div className="font-mono text-[10px] text-ink/50">{r.owner_phone || '—'}</div>
                </td>
                <td className="px-3 py-2 uppercase text-[11px] tracking-wider font-black text-ink/60">{r.category || '—'}</td>
                <td className="px-3 py-2 text-center">
                  {r.tos_signed
                    ? <CheckCircle2 data-testid={`heatmap-tos-${r.store_id}`} className="w-4 h-4 inline text-[#1B5E20]"/>
                    : <AlertTriangle data-testid={`heatmap-tos-${r.store_id}`} className="w-4 h-4 inline text-[#B71C1C]"/>}
                </td>
                <td className="px-3 py-2 text-center">
                  {r.vault_on_file
                    ? <ShieldCheck className="w-4 h-4 inline text-[#1B5E20]"/>
                    : <AlertTriangle className="w-4 h-4 inline text-[#B71C1C]"/>}
                </td>
                <td className="px-3 py-2 font-mono text-[11px]">{r.drug_license_number || '—'}</td>
                <td className="px-3 py-2 text-[11px]">
                  {r.drug_license_expiry
                    ? <span className={
                        r.dl_status === 'expired' ? 'text-[#B71C1C] font-black' :
                        r.dl_status === 'expiring_soon' ? 'text-[#8D3A0A] font-black' :
                        'text-ink/70'
                      }>{r.drug_license_expiry?.slice(0, 10)} {r.dl_days_left != null && <span className="text-[9px] text-ink/40">({r.dl_days_left}d)</span>}</span>
                    : '—'}
                </td>
                <td className="px-3 py-2 text-right font-mono text-[11px]">
                  {r.medical_orders_30d
                    ? <span>{r.evidence_pdf_30d}/{r.medical_orders_30d}</span>
                    : <span className="text-ink/40">—</span>}
                </td>
                <td className="px-3 py-2 text-center">
                  {r.status === 'suspended' ? (
                    <span className="inline-flex items-center gap-0.5 rounded-full bg-[#FFEBEE] text-[#B71C1C] border border-[#EF9A9A] px-2 py-0.5 text-[10px] font-black uppercase tracking-wider">Suspended</span>
                  ) : r.is_public ? (
                    <span className="inline-flex items-center rounded-full bg-[#E8F5E9] text-[#1B5E20] border border-[#A5D6A7] px-2 py-0.5 text-[10px] font-black uppercase tracking-wider">Public</span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-[#ECEFF1] text-[#455A64] border border-[#B0BEC5] px-2 py-0.5 text-[10px] font-black uppercase tracking-wider">Pending</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 text-[10px] text-ink/50">
        Generated {new Date(data.generated_at).toLocaleString()} · legend: <MapPin className="w-3 h-3 inline"/> vault · <ScrollText className="w-3 h-3 inline"/> ToS · <FileText className="w-3 h-3 inline"/> Evidence PDF.
      </div>
    </div>
  );
}
